import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Shield, Crown, Sun, Moon, Cross, Star, Bird } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import SEOHead from './SEOHead';

const DesprePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Despre Cuvânt și Rugăciune",
    "description": "Descoperă povestea din spatele podcastului Cuvânt și Rugăciune cu Mircea Stanciu - studii biblice profunde și rugăciune contemplativă",
    "about": {
      "@type": "Person",
      "@id": "https://cuvantsirugaciune.ro/despre#mircea-stanciu",
      "name": "Mircea Stanciu",
      "description": "Pasionat de studiul Scripturii și devotat în slujirea comunității creștine",
      "knowsAbout": ["Biblie", "Rugăciune", "Teologie Creștină", "Studii Biblice"]
    },
    "publisher": {
      "@type": "Organization",
      "name": "Cuvânt și Rugăciune",
      "url": "https://cuvantsirugaciune.ro"
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  return (
    <div className="bg-slate-900 text-white overflow-hidden">
      <SEOHead 
        title="Despre Cuvânt și Rugăciune cu Mircea Stanciu - Podcast Creștin Român"
        description="Descoperă povestea din spatele podcastului Cuvânt și Rugăciune cu Mircea Stanciu. Studii biblice profunde, rugăciune contemplativă și hrană spirituală pentru sufletul însetat de adevăr."
        keywords="despre mircea stanciu, podcast crestin roman, studii biblice, rugaciune, teologie crestina, biblie, spiritualitate crestina, romania"
        canonicalUrl="/despre"
        ogTitle="Despre Cuvânt și Rugăciune - Podcast Creștin cu Mircea Stanciu"
        ogDescription="Explorează călătoria spirituală prin Scripturi cu Mircea Stanciu. Studii biblice profunde și rugăciune contemplativă pentru creștinii români."
        structuredData={structuredData}
      />
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.8)), url('https://images.pexels.com/photos/18653022/pexels-photo-18653022.jpeg')`
          }}
        />
        
        {/* Floating Christian symbols */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            className="absolute top-20 left-10 text-amber-400/20"
          >
            <Cross className="w-8 h-8" />
          </motion.div>
          <motion.div
            animate={{ y: [0, 15, 0], rotate: [0, -3, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 1 }}
            className="absolute top-32 right-16 text-amber-400/20"
          >
            <Bird className="w-6 h-6" />
          </motion.div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex justify-center items-center space-x-4 mb-6">
              <Shield className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
              <Cross className="w-10 h-10 sm:w-16 sm:h-16 text-amber-400" />
              <Bird className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
            </div>
            
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent leading-tight">
              Despre Noi
            </h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-3 sm:mb-4 leading-relaxed px-4"
            >
              O călătorie spirituală prin Scripturi cu Mircea Stanciu
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16 sm:py-20 bg-slate-800/50 relative">
        <div className="absolute top-10 left-4 text-amber-400/10">
          <Sun className="w-16 h-16" />
        </div>
        <div className="absolute bottom-10 right-4 text-amber-400/10">
          <Moon className="w-12 h-12" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center"
          >
            <motion.div variants={fadeInUp}>
              <div className="flex items-center space-x-3 mb-4">
                <Heart className="w-8 h-8 text-amber-400" />
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400">Despre Podcast</h2>
              </div>
              <p className="text-base sm:text-lg text-slate-300 mb-4 sm:mb-6 leading-relaxed">
                "Cuvânt și Rugăciune" este o călătorie spirituală prin Scripturi, 
                unde explorăm împreună înțelepciunea veche și relevanța ei pentru viața noastră de astăzi.
              </p>
              <p className="text-base sm:text-lg text-slate-300 mb-6 leading-relaxed">
                Fiecare episod combină studiul biblic profund cu rugăciunea contemplativă, 
                oferind hrană spirituală pentru sufletul însetat de adevăr.
              </p>
              
              <div className="bg-slate-700/50 p-4 sm:p-6 rounded-2xl border border-amber-400/20">
                <div className="flex items-center space-x-2 mb-3">
                  <Crown className="w-6 h-6 text-amber-400" />
                  <h3 className="text-lg sm:text-xl font-semibold text-amber-400">Mircea Stanciu</h3>
                </div>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Pasionat de studiul Scripturii și devotat în slujirea comunității creștine, 
                  împărtășesc cu dragoste și înțelepciune bogățiile Cuvântului lui Dumnezeu 
                  prin aceste podcasturi inspiraționale.
                </p>
              </div>
            </motion.div>
            
            <motion.div 
              variants={fadeInUp}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-transparent rounded-3xl"></div>
              <img 
                src="https://images.pexels.com/photos/18653022/pexels-photo-18653022.jpeg"
                alt="Mircea Stanciu - Podcast Cuvânt și Rugăciune"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
          </motion.div>

          {/* Mission Section */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 sm:mt-20 text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-amber-400 mb-8">Misiunea Noastră</h2>
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-slate-700/50 p-6 rounded-2xl border border-amber-400/20">
                <Sun className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-amber-400 mb-3">Lumina Adevărului</h3>
                <p className="text-slate-300">Să aducem lumina Cuvântului lui Dumnezeu în viața fiecărui ascultător</p>
              </div>
              <div className="bg-slate-700/50 p-6 rounded-2xl border border-amber-400/20">
                <Heart className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-amber-400 mb-3">Comunitate</h3>
                <p className="text-slate-300">Să construim o comunitate unită în credință și rugăciune</p>
              </div>
              <div className="bg-slate-700/50 p-6 rounded-2xl border border-amber-400/20">
                <Crown className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-amber-400 mb-3">Creștere Spirituală</h3>
                <p className="text-slate-300">Să facilităm creșterea spirituală prin studiul profund al Bibliei</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default DesprePage;